import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Calendar, AlertCircle, Clock, TrendingUp, ArrowRight, AlertTriangle, Building2, Users, UserCheck, CheckCircle2 } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, differenceInDays, isPast } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ onLogout }) => {
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [incompleteAccounts, setIncompleteAccounts] = useState([]);
  const [myAssignedAccounts, setMyAssignedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // allSettled: if one of these fails, the others still show
      // (previously one failure blanked the whole dashboard, including follow-up visits)
      const [visitsRes, incompleteRes, assignedRes] = await Promise.allSettled([
        axios.get(`${API}/dashboard/upcoming-visits`, { headers }),
        axios.get(`${API}/accounts/incomplete`, { headers }),
        axios.get(`${API}/accounts/my-assigned`, { headers })
      ]);
      if (visitsRes.status === 'fulfilled') setUpcomingVisits(visitsRes.value.data);
      else toast.error('Could not load your follow-up visits. Please refresh.');
      if (incompleteRes.status === 'fulfilled') setIncompleteAccounts(incompleteRes.value.data);
      if (assignedRes.status === 'fulfilled') setMyAssignedAccounts(assignedRes.value.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVisitStatus = (visitDate) => {
    const date = parseISO(visitDate);
    if (isPast(date) && !isToday(date)) {
      return { label: 'OVERDUE', color: 'bg-gradient-to-r from-red-500 to-rose-500 text-white', icon: AlertCircle };
    }
    if (isToday(date)) {
      return { label: 'TODAY', color: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white', icon: AlertCircle };
    }
    if (isTomorrow(date)) {
      return { label: 'TOMORROW', color: 'bg-gradient-to-r from-violet-500 to-purple-500 text-white', icon: Clock };
    }
    const days = differenceInDays(date, new Date());
    if (days <= 7) {
      return { label: `IN ${days} DAYS`, color: 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white', icon: Clock };
    }
    return { label: format(date, 'MMM dd'), color: 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white', icon: Calendar };
  };

  const overdueVisits = upcomingVisits.filter(v => {
    const date = parseISO(v.next_visit_date);
    return isPast(date) && !isToday(date);
  });

  const todayVisits = upcomingVisits.filter(v => isToday(parseISO(v.next_visit_date)));
  const thisWeekVisits = upcomingVisits.filter(v => {
    const date = parseISO(v.next_visit_date);
    const days = differenceInDays(date, new Date());
    return days > 0 && days <= 7;
  });

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-base text-slate-500 mt-2">
            Welcome back! Here's your sales overview
          </p>
        </div>

        {/* Alert Banners */}
        {overdueVisits.length > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl shadow-lg shadow-red-500/25" data-testid="overdue-alert">
            <div className="flex items-center gap-3 text-white">
              <div className="p-2 bg-white/20 rounded-xl">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Overdue Visits!</h3>
                <p className="text-white/90">
                  You have {overdueVisits.length} overdue visit{overdueVisits.length > 1 ? 's' : ''} that need immediate attention.
                </p>
              </div>
            </div>
          </div>
        )}

        {todayVisits.length > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-lg shadow-amber-500/25" data-testid="today-alert">
            <div className="flex items-center gap-3 text-white">
              <div className="p-2 bg-white/20 rounded-xl">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Visits Due Today!</h3>
                <p className="text-white/90">
                  You have {todayVisits.length} visit{todayVisits.length > 1 ? 's' : ''} scheduled for today.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Overdue Card */}
          <div className={`rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
            overdueVisits.length > 0
              ? 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-xl shadow-red-500/30'
              : 'bg-white border border-slate-200 shadow-lg'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-semibold uppercase tracking-wider ${overdueVisits.length > 0 ? 'text-white/80' : 'text-slate-500'}`}>
                  Overdue
                </p>
                <p className={`text-4xl font-bold mt-2 ${overdueVisits.length > 0 ? 'text-white' : 'text-slate-800'}`} data-testid="overdue-count">
                  {overdueVisits.length}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${overdueVisits.length > 0 ? 'bg-white/20' : 'bg-red-50'}`}>
                <AlertCircle className={`h-8 w-8 ${overdueVisits.length > 0 ? 'text-white' : 'text-red-400'}`} />
              </div>
            </div>
          </div>

          {/* Today Card */}
          <div className={`rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
            todayVisits.length > 0
              ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-xl shadow-amber-500/30'
              : 'bg-white border border-slate-200 shadow-lg'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-semibold uppercase tracking-wider ${todayVisits.length > 0 ? 'text-white/80' : 'text-slate-500'}`}>
                  Due Today
                </p>
                <p className={`text-4xl font-bold mt-2 ${todayVisits.length > 0 ? 'text-white' : 'text-slate-800'}`} data-testid="today-count">
                  {todayVisits.length}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${todayVisits.length > 0 ? 'bg-white/20' : 'bg-amber-50'}`}>
                <Clock className={`h-8 w-8 ${todayVisits.length > 0 ? 'text-white' : 'text-amber-400'}`} />
              </div>
            </div>
          </div>

          {/* This Week Card */}
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl p-6 shadow-xl shadow-teal-500/30 transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-white/80">This Week</p>
                <p className="text-4xl font-bold mt-2" data-testid="this-week-count">
                  {thisWeekVisits.length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-white/20">
                <Calendar className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          {/* Pending Tasks Card */}
          <div className={`rounded-2xl p-6 transition-all duration-300 hover:scale-105 cursor-pointer ${
            incompleteAccounts.length > 0
              ? 'bg-gradient-to-br from-amber-500 to-yellow-600 text-white shadow-xl shadow-amber-500/30'
              : 'bg-white border border-slate-200 shadow-lg'
          }`} onClick={() => incompleteAccounts.length > 0 && document.getElementById('pending-tasks')?.scrollIntoView({ behavior: 'smooth' })}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-semibold uppercase tracking-wider ${incompleteAccounts.length > 0 ? 'text-white/80' : 'text-slate-500'}`}>
                  Pending Tasks
                </p>
                <p className={`text-4xl font-bold mt-2 ${incompleteAccounts.length > 0 ? 'text-white' : 'text-slate-800'}`} data-testid="pending-tasks-count">
                  {incompleteAccounts.length}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${incompleteAccounts.length > 0 ? 'bg-white/20' : 'bg-amber-50'}`}>
                <AlertTriangle className={`h-8 w-8 ${incompleteAccounts.length > 0 ? 'text-white' : 'text-amber-400'}`} />
              </div>
            </div>
          </div>
          {/* Pending Approvals — admin */}
          {isAdmin && (
            <div className={`rounded-2xl p-6 transition-all duration-300 hover:scale-105 cursor-pointer ${
              pendingApprovals.length > 0 ? 'bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-xl shadow-violet-500/30' : 'bg-white border border-slate-200 shadow-lg'
            }`} onClick={() => navigate('/approvals')}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-semibold uppercase tracking-wider ${pendingApprovals.length > 0 ? 'text-white/80' : 'text-slate-500'}`}>Pending Approvals</p>
                  <p className={`text-4xl font-bold mt-2 ${pendingApprovals.length > 0 ? 'text-white' : 'text-slate-800'}`}>{pendingApprovals.length}</p>
                </div>
                <div className={`p-3 rounded-xl ${pendingApprovals.length > 0 ? 'bg-white/20' : 'bg-violet-50'}`}>
                  <CheckCircle2 className={`h-8 w-8 ${pendingApprovals.length > 0 ? 'text-white' : 'text-violet-400'}`} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pending Approvals Section — admin inline approve/reject */}
        {isAdmin && pendingApprovals.length > 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-violet-200 shadow-xl mb-8">
            <div className="p-6 border-b border-violet-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-xl"><CheckCircle2 className="h-5 w-5 text-violet-700" /></div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Pending Changes</h2>
                  <p className="text-sm text-slate-500">{pendingApprovals.length} request{pendingApprovals.length !== 1 ? 's' : ''} waiting</p>
                </div>
              </div>
              <button onClick={() => navigate('/approvals')} className="text-xs text-violet-700 font-semibold hover:underline">View All →</button>
            </div>
            <div className="divide-y divide-slate-100">
              {pendingApprovals.slice(0, 5).map(req => (
                <div key={req.id} className="p-4 flex items-start justify-between gap-4 hover:bg-slate-50/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-slate-800 text-sm">{req.account_name}</p>
                      <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-lg">{req.change_type === 'lead' ? '📋 Lead' : '🏥 Account'}</span>
                      <span className="text-xs text-slate-400">by {req.requested_by_name || req.requested_by}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(req.field_changes || {}).map(([field, change]) => (
                        <span key={field} className="text-xs text-slate-500">
                          <span className="font-medium text-slate-700">{field.replace(/_/g,' ')}</span>:&nbsp;
                          <span className="line-through text-red-400">{String(change.old_value||'—')}</span>&nbsp;→&nbsp;
                          <span className="text-emerald-600 font-medium">{String(change.new_value||'—')}</span>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{req.requested_at ? new Date(req.requested_at).toLocaleString('en-IN') : ''}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${API}/change-requests/${req.id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Approved ✅');
                        setPendingApprovals(prev => prev.filter(r => r.id !== req.id));
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl">✓ Approve</button>
                    <button onClick={async () => {
                      const reason = window.prompt('Reason for rejection (optional):');
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${API}/change-requests/${req.id}/reject`, { reason: reason || '' }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Rejected');
                        setPendingApprovals(prev => prev.filter(r => r.id !== req.id));
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }} className="px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold rounded-xl">✕ Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Change Requests — non-admin */}
        {!isAdmin && myRequests.length > 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-teal-200 shadow-xl mb-8">
            <div className="p-6 border-b border-teal-100 flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-xl"><CheckCircle2 className="h-5 w-5 text-teal-700" /></div>
              <div>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>My Change Requests</h2>
                <p className="text-sm text-slate-500">Status of your recent edit submissions</p>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {myRequests.slice(0, 10).map(req => (
                <div key={req.id} className="p-4 flex items-start justify-between gap-3 hover:bg-slate-50/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-slate-800 text-sm">{req.account_name}</p>
                      <span className="text-xs text-slate-400">{req.change_type === 'lead' ? '📋 Lead' : '🏥 Account'}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-1">
                      {Object.entries(req.field_changes || {}).map(([field, change]) => (
                        <span key={field} className="text-xs text-slate-500">
                          <span className="font-medium">{field.replace(/_/g,' ')}</span>: {String(change.new_value||'—')}
                        </span>
                      ))}
                    </div>
                    {req.rejection_reason && (
                      <p className="text-xs text-red-500 mt-1">Reason: {req.rejection_reason}</p>
                    )}
                    <p className="text-xs text-slate-400">{req.requested_at ? new Date(req.requested_at).toLocaleString('en-IN') : ''}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-lg font-semibold flex-shrink-0 ${
                    req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                    req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {req.status === 'approved' ? '✅ Approved' : req.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Assigned Accounts — Action Required */}
        {!isAdmin && myAssignedAccounts.length > 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-teal-200 shadow-xl mb-8">
            <div className="p-6 border-b border-teal-100 flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-xl">
                <UserCheck className="h-5 w-5 text-teal-700" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  My Assigned Accounts
                </h2>
                <p className="text-sm text-slate-500">
                  {myAssignedAccounts.filter(a => a.has_missing).length > 0
                    ? `${myAssignedAccounts.filter(a => a.has_missing).length} account(s) need your attention`
                    : `All ${myAssignedAccounts.length} accounts are complete`}
                </p>
              </div>
            </div>
            <div className="p-6 space-y-3">
              {myAssignedAccounts.map(account => (
                <div
                  key={account.id}
                  onClick={() => navigate(`/accounts/${account.id}`)}
                  className={`group flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                    account.has_missing
                      ? 'bg-amber-50 border-amber-200 hover:border-amber-400'
                      : 'bg-emerald-50 border-emerald-200 hover:border-emerald-400'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${account.has_missing ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                      {account.has_missing
                        ? <AlertTriangle className="h-5 w-5 text-amber-600" />
                        : <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      }
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 group-hover:text-teal-700 transition-colors">
                        {account.name}
                      </h3>
                      {account.has_missing ? (
                        <p className="text-xs text-amber-600 mt-1">
                          Missing: {account.missing_fields.join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-emerald-600 mt-1">All details complete ✓</p>
                      )}
                    </div>
                  </div>
                  <ArrowRight className={`h-5 w-5 transition-all group-hover:translate-x-1 ${account.has_missing ? 'text-amber-300 group-hover:text-amber-500' : 'text-emerald-300 group-hover:text-emerald-500'}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Tasks - Incomplete Accounts */}
        {incompleteAccounts.length > 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-amber-200 shadow-xl mb-8" id="pending-tasks">
            <div className="p-6 border-b border-amber-100 flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Pending Tasks
                </h2>
                <p className="text-sm text-slate-500">Accounts with missing contact details (Designation / Phone Number / Can Influence Decisions)</p>
              </div>
            </div>
            <div className="p-6 space-y-3" data-testid="pending-tasks-list">
              {incompleteAccounts.map((account) => {
                const incompleteContacts = account.contacts?.filter(c => !c.can_influence_decisions || !c.designation || c.designation === 'TBD' || !c.whatsapp_number) || [];
                return (
                  <div
                    key={account.id}
                    onClick={() => navigate(`/accounts/${account.id}`)}
                    className="group flex items-center justify-between p-4 bg-white rounded-xl border border-amber-200 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer"
                    data-testid="pending-task-card"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <Building2 className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 group-hover:text-teal-700 transition-colors">{account.name}</h3>
                        <p className="text-xs text-amber-600 mt-1">
                          {incompleteContacts.length} contact{incompleteContacts.length > 1 ? 's' : ''} missing Designation / Phone / Can Influence Decisions
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Visits List */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl">
          <div className="p-6 border-b border-slate-200/50">
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Visits Overview
            </h2>
          </div>

          <div className="p-6" data-testid="upcoming-visits-list">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent"></div>
                <p className="text-slate-500 mt-4">Loading visits...</p>
              </div>
            ) : upcomingVisits.length === 0 ? (
              <div className="text-center py-12">
                <div className="p-4 bg-slate-100 rounded-full inline-block mb-4">
                  <Calendar className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-500 text-lg">No visits scheduled yet</p>
                <p className="text-slate-400 text-sm mt-2">Register a new call to schedule visits</p>
              </div>
            ) : (
              <div className="space-y-8">

                {/* ── OVERDUE SECTION ── */}
                {overdueVisits.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse"></div>
                      <h3 className="text-sm font-bold text-red-600 uppercase tracking-widest">
                        Overdue — {overdueVisits.length} visit{overdueVisits.length > 1 ? 's' : ''} need attention
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {overdueVisits.map((visit) => {
                        const status = getVisitStatus(visit.next_visit_date);
                        const StatusIcon = status.icon;
                        const daysOverdue = differenceInDays(new Date(), parseISO(visit.next_visit_date));
                        return (
                          <div
                            key={visit.id}
                            onClick={() => navigate(`/accounts/${visit.account_id}`)}
                            className="group flex items-center justify-between p-5 bg-red-50 rounded-xl border border-red-200 hover:border-red-400 hover:shadow-lg hover:shadow-red-500/10 transition-all duration-300 cursor-pointer"
                            data-testid="visit-card-overdue"
                          >
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-slate-800 group-hover:text-red-700 transition-colors">
                                {visit.account_name}
                              </h3>
                              <p className="text-sm text-slate-500 mt-1">Contact: {visit.contact_person_name}</p>
                              <p className="text-xs text-red-500 font-medium mt-1">
                                {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm shadow-lg ${status.color}`}>
                                  <StatusIcon className="h-4 w-4" />
                                  {status.label}
                                </div>
                                <p className="text-xs text-slate-400 mt-2">
                                  Was due: {format(parseISO(visit.next_visit_date), 'EEEE, MMM dd')}
                                </p>
                              </div>
                              <ArrowRight className="h-5 w-5 text-red-300 group-hover:text-red-500 group-hover:translate-x-1 transition-all" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── UPCOMING SECTION ── */}
                {upcomingVisits.filter(v => !isPast(parseISO(v.next_visit_date)) || isToday(parseISO(v.next_visit_date))).length > 0 && (
                  <div>
                    {overdueVisits.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-3 w-3 rounded-full bg-teal-500"></div>
                        <h3 className="text-sm font-bold text-teal-700 uppercase tracking-widest">
                          Upcoming & Today
                        </h3>
                      </div>
                    )}
                    <div className="space-y-3">
                      {upcomingVisits
                        .filter(v => !isPast(parseISO(v.next_visit_date)) || isToday(parseISO(v.next_visit_date)))
                        .map((visit) => {
                          const status = getVisitStatus(visit.next_visit_date);
                          const StatusIcon = status.icon;
                          return (
                            <div
                              key={visit.id}
                              onClick={() => navigate(`/accounts/${visit.account_id}`)}
                              className="group flex items-center justify-between p-5 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-300 cursor-pointer"
                              data-testid="visit-card"
                            >
                              <div className="flex-1">
                                <h3 className="font-bold text-lg text-slate-800 group-hover:text-teal-700 transition-colors">
                                  {visit.account_name}
                                </h3>
                                <p className="text-sm text-slate-500 mt-1">Contact: {visit.contact_person_name}</p>
                                {visit.next_visit_planned_in && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    Planned: {visit.next_visit_planned_in} from last visit
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm shadow-lg ${status.color}`}>
                                    <StatusIcon className="h-4 w-4" />
                                    {status.label}
                                  </div>
                                  <p className="text-xs text-slate-400 mt-2">
                                    {format(parseISO(visit.next_visit_date), 'EEEE, MMM dd')}
                                  </p>
                                </div>
                                <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                              </div>
                            </div>
                          );
                      })}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
